import { useState } from 'react';
import { BarChart } from '@mui/icons-material';
import {
	FormActions,
	FormCard,
	FormField,
	FormInput,
	FormSectionTitle,
	useToast,
} from '@practium/ui';
import { useCorrectionStore } from '../../store/correctionStore';
import { SimpleDataTable } from '../Tables/SimpleDataTable/SimpleDataTable';
import { RichTextarea } from '../RichTextarea/RichTextarea';
import { useSaveCorrectionAdvice } from '../../hooks/useSaveCorrectionAdvice';
import { formatCalculationList } from '../../services/correctionSaveFeedback';
import {
	PRODUCT_TABLE_COLUMNS,
	mapProductTableRows,
	RESULT_TABLE_COLUMNS,
	mapResultTableRows,
} from '../../utils/correctionTableMapper';
import { PanelActions } from '../PanelActions/PanelActions';
import styles from './TabPanel.module.css';

interface RecommendationTabProps {
	canEdit?: boolean;
}

export const RecommendationTab = ({ canEdit = true }: RecommendationTabProps) => {
	const { recommendation, setRecommendationField } = useCorrectionStore();
	const toast = useToast();
	const { save } = useSaveCorrectionAdvice();
	const [isEditing, setIsEditing] = useState(() => !recommendation.identificacao);
	const [isSaving, setIsSaving] = useState(false);

	const handleSave = async () => {
		if (!recommendation.identificacao.trim()) {
			toast.error('Identificação é obrigatória.');
			return;
		}
		setIsSaving(true);
		try {
			const { calculations } = await save();
			const calculationList = formatCalculationList(calculations);
			toast.success(
				calculationList
					? `Recomendação salva com sucesso! Cálculos incluídos: ${calculationList}.`
					: 'Recomendação salva com sucesso!'
			);
			setIsEditing(false);
		} catch {
			toast.error('Erro ao salvar recomendação.');
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<div className={styles.panel}>
			<FormCard icon={<BarChart />} title="Recomendação">
				<div className={styles.form}>
					<div className={styles.section}>
						<FormSectionTitle>Produtos e Forma de Aplicação</FormSectionTitle>
						<div className={styles.sectionFields}>
							<FormField label="Identificação" required>
								{isEditing ? (
									<FormInput
										placeholder="Ex.: Recomendação completa para Maria Ferraz - 2026 - Dr. Carlos Silva"
										value={recommendation.identificacao}
										onChange={(e) => setRecommendationField('identificacao', e.target.value)}
									/>
								) : (
									<span className={styles.viewText}>
										{recommendation.identificacao ||
											'Recomendação completa para Maria Ferraz - 2026 - Dr. Carlos Silva'}
									</span>
								)}
							</FormField>

							{recommendation.products.length > 0 ? (
								<SimpleDataTable
									columns={PRODUCT_TABLE_COLUMNS}
									rows={mapProductTableRows(recommendation.products)}
								/>
							) : (
								<span className={styles.viewText}>Nenhum produto recomendado.</span>
							)}

							<FormField label="Observações">
								{isEditing ? (
									<RichTextarea
										value={recommendation.observacoes}
										onChange={(v) => setRecommendationField('observacoes', v)}
										placeholder="Adicione uma observação aqui..."
									/>
								) : (
									<RichTextarea value={recommendation.observacoes} onChange={() => {}} readOnly />
								)}
							</FormField>
						</div>
					</div>

					{recommendation.resultTable.length > 0 && (
						<div className={styles.section}>
							<FormSectionTitle>Resultado da Correção</FormSectionTitle>
							<SimpleDataTable columns={RESULT_TABLE_COLUMNS} rows={mapResultTableRows(recommendation.resultTable)} />
						</div>
					)}
				</div>
			</FormCard>

			<PanelActions>
				{isEditing ? (
					<FormActions
						onCancel={() => setIsEditing(false)}
						onSave={handleSave}
						isLoading={isSaving}
					/>
				) : (
					<FormActions mode="view" onEdit={canEdit ? () => setIsEditing(true) : undefined} />
				)}
			</PanelActions>
		</div>
	);
};
